import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./CurrencyPairManagementPage.css";
import "./AddCurrencyPairPage.css";

const AVAILABILITY_OPTIONS = [
  { value: "GLOBAL", label: "GLOBAL (Binance Data)" },
  { value: "LOCAL", label: "LOCAL" },
];
const PRICE_SOURCE_OPTIONS = [
  { value: "CMC", label: "CMC (CoinMarketCap)" },
  { value: "LOCAL", label: "LOCAL (Manual)" },
];

const AddCurrencyPairPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [coinsList, setCoinsList] = useState([]);
  const [symbolToId, setSymbolToId] = useState({});
  const [form, setForm] = useState({
    baseCurrency: "",
    quoteCurrency: "",
    availability: "GLOBAL",
    priceSource: "CMC",
    cmcId: "",
    pairType: false,
  });
  const [, setPairInfoData] = useState(null);
  const [pairForm, setPairForm] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successModal, setSuccessModal] = useState(null);

  const currencies = [...new Set(coinsList.map((c) => c.short_name).filter(Boolean))].sort();

  const fetchCurrencies = useCallback(async () => {
    setLoading(true);
    const res = await AdminService.getCoinsList();
    setLoading(false);
    if (res?.success && Array.isArray(res.data)) {
      setCoinsList(res.data);
      const map = {};
      res.data.forEach((c) => {
        if (c.short_name && !map[c.short_name]) map[c.short_name] = c._id;
      });
      setSymbolToId(map);
    } else {
      setCoinsList([]);
      setSymbolToId({});
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const updatePairForm = (key, value) => setPairForm((f) => (f ? { ...f, [key]: value } : null));

  const canProceedStep1 =
    form.baseCurrency &&
    form.quoteCurrency &&
    form.baseCurrency !== form.quoteCurrency &&
    !(form.availability === "LOCAL" && form.priceSource === "CMC" && !form.cmcId.trim());

  const handleFetchPairInfo = async (e) => {
    e?.preventDefault?.();
    setSubmitError("");
    setFetchLoading(true);
    try {
      const available = form.availability;
      const body = {
        available: available,
        base_currency: form.baseCurrency,
        quote_currency: form.quoteCurrency,
        base_currency_id: symbolToId[form.baseCurrency] || undefined,
        quote_currency_id: symbolToId[form.quoteCurrency] || undefined,
      };
      if (available === "LOCAL") {
        body.price_source = form.priceSource;
        if (form.priceSource === "CMC") {
          body.cmc_id = form.cmcId.trim();
        }
      }
      const res = await AdminService.getPairInfo(body);
      setFetchLoading(false);
      if (res?.success && res.data) {
        setPairInfoData(res.data);
        setPairForm({
          base_currency_id: res.data.base_currency_id || symbolToId[form.baseCurrency],
          quote_currency_id: res.data.quote_currency_id || symbolToId[form.quoteCurrency],
          available,
          price_source: res.data.price_source || (available === "GLOBAL" ? "BINANCE" : form.priceSource),
          cmc_id: form.priceSource === "CMC" ? form.cmcId.trim() : "",
          pairType: Boolean(form.pairType),
          buy_price: res.data.buy_price ?? "",
          sell_price: res.data.sell_price ?? "",
          maker_fee: res.data.maker_fee ?? 0.1,
          taker_fee: res.data.taker_fee ?? 0.1,
          min_order_qty: res.data.min_order_qty ?? "",
          max_order_qty: res.data.max_order_qty ?? "",
          step_size: res.data.step_size ?? "",
          tick_size: res.data.tick_size ?? "",
          min_notional: res.data.min_notional ?? "",
          base_asset_precision: res.data.base_asset_precision ?? 8,
          quote_asset_precision: res.data.quote_asset_precision ?? 8,
        });
        setStep(2);
      } else {
        setSubmitError(res?.message || "Pair not found. Check currencies and availability.");
      }
    } catch (err) {
      setFetchLoading(false);
      setSubmitError("Failed to fetch pair info. Please try again.");
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => s - 1);
      if (step === 2) {
        setPairInfoData(null);
        setPairForm(null);
      }
      setSubmitError("");
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!pairForm) return;
    if (!pairForm.base_currency_id || !pairForm.quote_currency_id) {
      setSubmitError("Base or quote currency not found. Add the coin in Currency Management first.");
      return;
    }
    setSubmitError("");
    setSubmitLoading(true);
    try {
      const payload = {
        base_currency_id: pairForm.base_currency_id,
        quote_currency_id: pairForm.quote_currency_id,
        available: pairForm.available,
        price_source: pairForm.price_source,
        ...(pairForm.price_source === "CMC" && pairForm.cmc_id?.trim() ? { cmc_id: pairForm.cmc_id.trim() } : {}),
        pairType: Boolean(pairForm.pairType),
        buy_price: Number(pairForm.buy_price),
        sell_price: Number(pairForm.sell_price),
        maker_fee: Number(pairForm.maker_fee) || 0.1,
        taker_fee: Number(pairForm.taker_fee) || 0.1,
        min_order_qty: Number(pairForm.min_order_qty),
        max_order_qty: Number(pairForm.max_order_qty),
        step_size: Number(pairForm.step_size),
        tick_size: Number(pairForm.tick_size),
        min_notional: Number(pairForm.min_notional),
        base_asset_precision: Number(pairForm.base_asset_precision) || 8,
        quote_asset_precision: Number(pairForm.quote_asset_precision) || 8,
      };
      const res = await AdminService.createPair(payload);
      setSubmitLoading(false);
      if (res?.success) {
        setSuccessModal({
          pairName: `${form.baseCurrency}/${form.quoteCurrency}`,
          availability: form.availability === "GLOBAL" ? "GLOBAL (Binance)" : `LOCAL (${form.priceSource})`,
        });
      } else {
        setSubmitError(res?.message || "Failed to create pair.");
      }
    } catch (err) {
      setSubmitLoading(false);
      setSubmitError("An error occurred. Please try again.");
    }
  };

  const handleSuccessClose = () => {
    setSuccessModal(null);
    setForm({
      baseCurrency: "",
      quoteCurrency: "",
      availability: "GLOBAL",
      priceSource: "CMC",
      cmcId: "",
      pairType: false,
    });
    setPairInfoData(null);
    setPairForm(null);
    setStep(1);
  };

  if (loading) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <Link to="/dashboard/currencypair">Currency Pair Management</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Add Currency Pair</span>
            </p>
            <h1 className="dashboard-hero__title">Add Currency Pair</h1>
          </div>
        </section>
        <div className="addpair-loading">
          <i className="fas fa-spinner fa-spin" /> Loading…
        </div>
      </main>
    );
  }

  if (currencies.length === 0) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <Link to="/dashboard/currencypair">Currency Pair Management</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Add Currency Pair</span>
            </p>
            <h1 className="dashboard-hero__title">Add Currency Pair</h1>
          </div>
        </section>
        <div className="addpair-loading">
          <p>No currencies found. Add coins first from Currency Management.</p>
          <Link to="/dashboard/currencymanagement" className="cp-btn cp-btn--primary">Go to Currency Management</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="dashboard-main dashboard-subpage addpair-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/currencypair">Currency Pair Management</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Add Currency Pair</span>
          </p>
          <h1 className="dashboard-hero__title">Add Currency Pair</h1>
          <p className="dashboard-hero__subtitle">Create a new trading pair with base and quote currencies</p>
        </div>
        <div className="dashboard-hero__icon addpair-hero__icon">
          <i className="fas fa-plus-circle" />
        </div>
      </section>

      <div className="dashboard-body addpair-body">
        <div className="addpair-card">
          <div className="cp-stepper addpair-stepper">
            <div className={`cp-stepper-item ${step >= 1 ? "active" : ""}`}>
              <span className="cp-stepper-num">{step > 1 ? <i className="fas fa-check" /> : "1"}</span>
              <span className="cp-stepper-label">Select Currencies</span>
            </div>
            <div className="cp-stepper-line" />
            <div className={`cp-stepper-item ${step >= 2 ? "active" : ""}`}>
              <span className="cp-stepper-num">{step > 2 ? <i className="fas fa-check" /> : "2"}</span>
              <span className="cp-stepper-label">Configure</span>
            </div>
            <div className="cp-stepper-line" />
            <div className={`cp-stepper-item ${step >= 3 ? "active" : ""}`}>
              <span className="cp-stepper-num">3</span>
              <span className="cp-stepper-label">Confirm</span>
            </div>
          </div>

          {submitError && (
            <div className="addpair-alert addpair-alert--error">
              <i className="fas fa-exclamation-circle" />
              <span>{submitError}</span>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleFetchPairInfo} className="addpair-form">
              <div className="cp-field">
                <label>Base Currency <span className="required">*</span></label>
                <div className="cp-input-wrap">
                  <i className="fas fa-gem" />
                  <select
                    value={form.baseCurrency}
                    onChange={(e) => updateForm("baseCurrency", e.target.value)}
                    required
                  >
                    <option value="">Select Base Currency</option>
                    {currencies.filter((c) => c !== form.quoteCurrency).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="cp-field">
                <label>Quote Currency <span className="required">*</span></label>
                <div className="cp-input-wrap">
                  <i className="fas fa-gem" />
                  <select
                    value={form.quoteCurrency}
                    onChange={(e) => updateForm("quoteCurrency", e.target.value)}
                    required
                  >
                    <option value="">Select Quote Currency</option>
                    {currencies.filter((c) => c !== form.baseCurrency).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="cp-field">
                <label>Availability <span className="required">*</span></label>
                <div className="cp-input-wrap">
                  <i className="fas fa-globe" />
                  <select
                    value={form.availability}
                    onChange={(e) => updateForm("availability", e.target.value)}
                  >
                    {AVAILABILITY_OPTIONS.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {form.availability === "LOCAL" && (
                <div className="cp-field">
                  <label>Price Source <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-chart-line" />
                    <select
                      value={form.priceSource}
                      onChange={(e) => updateForm("priceSource", e.target.value)}
                    >
                      {PRICE_SOURCE_OPTIONS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {form.availability === "LOCAL" && form.priceSource === "CMC" && (
                <div className="cp-field">
                  <label>CMC ID <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-hashtag" />
                    <input
                      type="text"
                      value={form.cmcId}
                      onChange={(e) => updateForm("cmcId", e.target.value)}
                      placeholder="Enter CoinMarketCap ID"
                      required
                    />
                  </div>
                </div>
              )}
              <div className="cp-field">
                <label>Is Meme Pair <span className="required">*</span></label>
                <div className="cp-input-wrap">
                  <i className="fas fa-tags" />
                  <select
                    value={form.pairType ? "true" : "false"}
                    onChange={(e) => updateForm("pairType", e.target.value === "true")}
                    required
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>
              </div>
              <div className="addpair-actions">
                <Link to="/dashboard/currencypair" className="cp-btn cp-btn--outline">Cancel</Link>
                <button type="submit" className="cp-btn cp-btn--primary" disabled={!canProceedStep1 || fetchLoading}>
                  {fetchLoading ? <><i className="fas fa-spinner fa-spin" /> Fetching…</> : <>Continue <i className="fas fa-arrow-right" /></>}
                </button>
              </div>
            </form>
          )}

          {step === 2 && pairForm && (
            <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="addpair-form">
              <p className="cp-step-desc">Edit values if needed. Data prefilled from {form.availability === "GLOBAL" ? "Binance" : form.priceSource === "CMC" ? "CMC" : "manual"}.</p>
              <div className="addpair-config-grid">
                <div className="cp-field">
                  <label>Buy Price</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.buy_price} onChange={(e) => updatePairForm("buy_price", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Sell Price</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.sell_price} onChange={(e) => updatePairForm("sell_price", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Maker Fee (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.01" min={0} value={pairForm.maker_fee} onChange={(e) => updatePairForm("maker_fee", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Taker Fee (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.01" min={0} value={pairForm.taker_fee} onChange={(e) => updatePairForm("taker_fee", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Min Order Qty</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.min_order_qty} onChange={(e) => updatePairForm("min_order_qty", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Max Order Qty</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.max_order_qty} onChange={(e) => updatePairForm("max_order_qty", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Step Size</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.step_size} onChange={(e) => updatePairForm("step_size", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Tick Size</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.tick_size} onChange={(e) => updatePairForm("tick_size", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Min Notional</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.min_notional} onChange={(e) => updatePairForm("min_notional", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Base Asset Precision</label>
                  <div className="cp-input-wrap">
                    <input type="number" min={0} max={18} value={pairForm.base_asset_precision} onChange={(e) => updatePairForm("base_asset_precision", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Quote Asset Precision</label>
                  <div className="cp-input-wrap">
                    <input type="number" min={0} max={18} value={pairForm.quote_asset_precision} onChange={(e) => updatePairForm("quote_asset_precision", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
              </div>
              <div className="addpair-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleBack}>
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <button type="submit" className="cp-btn cp-btn--primary">
                  Continue <i className="fas fa-arrow-right" />
                </button>
              </div>
            </form>
          )}

          {step === 3 && pairForm && (
            <div className="addpair-form">
              <p className="cp-step-desc">Confirm and create the trading pair.</p>
              <div className="addpair-summary">
                <div className="addpair-summary-row"><span>Pair</span><strong>{form.baseCurrency}/{form.quoteCurrency}</strong></div>
                <div className="addpair-summary-row"><span>Availability</span><strong>{form.availability}</strong></div>
                <div className="addpair-summary-row"><span>Is Meme Pair</span><strong>{pairForm.pairType ? "Yes" : "No"}</strong></div>
                <div className="addpair-summary-row"><span>Buy / Sell Price</span><strong>{pairForm.buy_price} / {pairForm.sell_price}</strong></div>
                <div className="addpair-summary-row"><span>Maker / Taker Fee</span><strong>{pairForm.maker_fee}% / {pairForm.taker_fee}%</strong></div>
              </div>
              <div className="addpair-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleBack}>
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <button type="button" className="cp-btn cp-btn--primary" onClick={handleSubmit} disabled={submitLoading}>
                  {submitLoading ? <><i className="fas fa-spinner fa-spin" /> Creating…</> : <><i className="fas fa-check" /> Create Pair</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {successModal && (
        <div className="addpair-success-overlay" onClick={handleSuccessClose}>
          <div className="addpair-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="addpair-success-icon"><i className="fas fa-check-circle" /></div>
            <h3>Pair Created Successfully</h3>
            <p>The following pair has been created:</p>
            <div className="addpair-success-details">
              <div className="addpair-success-row"><span>Pair</span><strong>{successModal.pairName}</strong></div>
              <div className="addpair-success-row"><span>Availability</span><strong>{successModal.availability}</strong></div>
            </div>
            <button type="button" className="cp-btn cp-btn--primary" onClick={handleSuccessClose}>Done</button>
          </div>
        </div>
      )}
    </main>
  );
};

export default AddCurrencyPairPage;
